// Sheet = Aurora <Drawer> (right on desktop, bottom on mobile) or Aurora <Modal> (`modal`, 520 px on desktop).
import { Drawer, DrawerBody, DrawerContent, DrawerHeader } from '@aurora-reactkit/components/drawer';
import { Modal, ModalContent, ModalTitle } from '@aurora-reactkit/components/modal';
import { useEffect, type ReactNode } from 'react';
import { useIsDesktop } from '../lib/useMediaQuery';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  variant?: 'modal' | 'drawer';
  testId?: string;
  children: ReactNode;
}

// Open sheets, oldest first (Escape closes the last one only).
const OPEN_SHEETS: symbol[] = [];

export function Sheet({ open, onClose, title, subtitle, variant = 'modal', testId, children }: Props) {
  const desktop = useIsDesktop();
  const onOpenChange = (o: boolean) => { if (!o) onClose(); };

  // Aurora's Modal ignores Escape while its opening animation runs; close on Escape from the first frame.
  // Only the topmost open sheet closes, so a sheet opened over another (authorisation over a fix) closes alone.
  useEffect(() => {
    if (!open) return;
    const me = Symbol('sheet');
    OPEN_SHEETS.push(me);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && OPEN_SHEETS[OPEN_SHEETS.length - 1] === me) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); OPEN_SHEETS.splice(OPEN_SHEETS.indexOf(me), 1); };
  }, [open, onClose]);

  if (variant === 'modal' && desktop)
    return (
      <Modal open={open} onOpenChange={onOpenChange}>
        <ModalContent data-testid={testId} closeButtonAriaLabel="Close" showCloseButton>
          <ModalTitle>{title}</ModalTitle>
          {children}
        </ModalContent>
      </Modal>
    );

  return (
    <Drawer open={open} onOpenChange={onOpenChange} side={desktop ? 'right' : 'bottom'}>
      <DrawerContent data-testid={testId} closeButtonLabel="Close" size="md">
        <DrawerHeader title={title} subtitle={subtitle} />
        <DrawerBody>{children}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
